import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import client from '../lib/feathers';

// The metrics we chart, in display order. Keys match admin/platform-metrics.
const METRICS = [
  { key: 'activeListings', label: 'Listings', color: '#16a34a' },
  { key: 'users', label: 'Users', color: '#2563eb' },
  { key: 'libations', label: 'Wines', color: '#7c3aed' },
  { key: 'libationVintages', label: 'Vintages', color: '#9333ea' },
  { key: 'cellarEntries', label: 'Cellar bottles', color: '#db2777' },
  { key: 'checkins', label: 'Tastings', color: '#ea580c' },
  { key: 'sips', label: 'Sips', color: '#0891b2' },
];

function fmt(n) {
  if (typeof n !== 'number') return '—';
  return n.toLocaleString();
}

function MetricCard({ metric, current, history }) {
  const series = (history || [])
    .map((h) => ({ date: h.snapshotDate, value: h[metric.key] }))
    .filter((p) => typeof p.value === 'number');
  return (
    <div className="border border-gray-200 rounded-lg p-3">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-sm text-gray-500">{metric.label}</span>
        <span className="text-2xl font-bold text-gray-900">{fmt(current)}</span>
      </div>
      {series.length >= 2 ? (
        <ResponsiveContainer width="100%" height={70}>
          <LineChart data={series} margin={{ top: 4, right: 4, left: 4, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f1f1" vertical={false} />
            <XAxis dataKey="date" hide />
            <YAxis hide domain={['dataMin', 'dataMax']} />
            <Tooltip
              formatter={(v) => [fmt(v), metric.label]}
              labelFormatter={(d) => d}
              contentStyle={{ fontSize: 12 }}
            />
            <Line type="monotone" dataKey="value" stroke={metric.color} strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="h-[70px] flex items-center justify-center text-xs text-gray-400 text-center">
          Growth chart builds as daily snapshots accumulate.
        </div>
      )}
    </div>
  );
}

export default function PlatformGrowthPanel() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [snapshotting, setSnapshotting] = useState(false);

  const load = async () => {
    try {
      setData(await client.service('admin/platform-metrics').find());
      setErr(null);
    } catch (e) {
      setErr(e.message || 'Failed to load platform metrics');
    }
  };

  useEffect(() => { load(); }, []);

  const snapshotNow = async () => {
    setSnapshotting(true);
    try {
      await client.service('admin/platform-metrics').create({});
      await load();
    } catch (e) {
      // non-fatal
    } finally {
      setSnapshotting(false);
    }
  };

  if (err) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-4 mb-4 text-sm text-gray-500">
        Platform Growth unavailable: {err}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-white shadow sm:rounded-lg p-4 mb-4 text-sm text-gray-500">
        Loading Platform Growth…
      </div>
    );
  }

  const { current, history } = data;

  return (
    <div className="bg-white shadow sm:rounded-lg p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Platform Growth</h2>
          <p className="text-xs text-gray-500">
            Top-line counts over time. {history && history.length < 2
              ? 'Charts fill in as daily snapshots accumulate.'
              : `${history.length} daily snapshots.`}
          </p>
        </div>
        <button
          type="button"
          onClick={snapshotNow}
          disabled={snapshotting}
          className="text-xs px-2 py-1 border border-gray-300 rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          title="Snapshot today's counts now (idempotent)"
        >
          {snapshotting ? 'Snapshotting…' : 'Snapshot now'}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {METRICS.map((m) => (
          <MetricCard key={m.key} metric={m} current={current[m.key]} history={history} />
        ))}
      </div>
    </div>
  );
}
